import { GetStaticPaths, GetStaticProps } from "next";
import NextImage from "next/image";
import Link from "next/link";
import {
    Breadcrumbs as Modes0Breadcrumbs,
    ContentArticleReader,
    ContentTitles,
    PageWithSidebarLayout,
    SidebarArticle,
    ServicesTrustHero,
    type Crumb,
    type ServicesTrustHeroImage,
    type SidebarArticleItem,
} from "@modes0/ui-components";
import { getServiceSubCategories, getServiceSubCategoryBySlug } from "@/content/services";
import { BUSINESS_NAME, createServiceContactFormContent, getCanonicalUrl } from "@/content";
import { getAllUnRelatedSubCategoriesByCategory, getRelatedSubCategoriesByCategory } from "@/lib";
import ServiceContactForm from "@/widgets/contact-form/ServiceContactForm";
import type { ContentBlock, Image as ContentImage, SubCategory } from "@/types";
import {PageSeo, buildBreadcrumbSchema, buildServiceSchema} from "@modes0/ui-components-seo";
import {getBreadcrumbSeoItems, getServiceSeoInput, seoSiteProfile} from "@/content/seo";

type ServicePageProps = {
    categorySlug: string;
    serviceSlug: string;
};

const brandColor = "#008e0e";
const pageBackgroundColor = "#ffffff";
const visibleSidebarItems = 4;

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const getCategoryHref = (service: SubCategory) => `/${service.headCategory.categoryType}/${service.headCategory.slug}`;

const getServiceHref = (service: SubCategory) => service.link || `${getCategoryHref(service)}/${service.slug}`;

const getImageSrc = (image?: Partial<ContentImage>) => image?.url || image?.original || image?.thumbnail || "";

const toContentImage = (
    service: SubCategory,
    image: Partial<ContentImage> | undefined,
    index = 0
): ContentImage | null => {
    const src = getImageSrc(image) || service.heroImage?.url || "";

    if (!src) {
        return null;
    }

    const title =
        image?.title || image?.originalTitle || image?.thumbnailTitle || service.heroImage?.title || service.title;
    const alt = image?.alt || image?.originalAlt || image?.thumbnailAlt || service.heroImage?.alt || title;

    return {
        ...image,
        id: image?.id || service.heroImage?.id || service.id || index + 1,
        url: image?.url || src,
        original: image?.original || src,
        thumbnail: image?.thumbnail || src,
        title,
        alt,
        originalAlt: image?.originalAlt || alt,
        thumbnailAlt: image?.thumbnailAlt || alt,
        thumbnailTitle: image?.thumbnailTitle || title,
    } as ContentImage;
};

const getServiceImages = (service: SubCategory) =>
    (service.images || [])
        .map((image, index) => toContentImage(service, image as Partial<ContentImage>, index))
        .filter((image): image is ContentImage => Boolean(image));

const getServiceImage = (service: SubCategory) => getServiceImages(service)[0] || toContentImage(service, undefined);

const getServiceAdvocateImage = (service: SubCategory): ServicesTrustHeroImage | undefined => {
    const image = getServiceImage(service);

    if (!image) {
        return undefined;
    }

    return {
        src: image.url,
        url: image.url,
        original: image.original,
        thumbnail: image.thumbnail,
        alt: image.alt || service.title,
        title: image.title || service.title,
        loading: "lazy",
    };
};

const getSidebarArticleImage = (service: SubCategory): SidebarArticleItem["image"] | undefined => {
    const image = getServiceImage(service);

    if (!image) {
        return undefined;
    }

    return {
        src: image.url,
        url: image.url,
        original: image.original,
        thumbnail: image.thumbnail,
        alt: image.alt || service.title,
        title: image.title || service.title,
    };
};

const getSidebarArticleItems = (services: SubCategory[]): SidebarArticleItem[] =>
    services.slice(0, visibleSidebarItems).map((service) => ({
        id: service.id,
        title: service.title,
        description: service.description,
        href: getServiceHref(service),
        ctaLabel: "View service",
        image: getSidebarArticleImage(service),
        accentColor: brandColor,
    }));

const getOrderedContentBlocks = (content: SubCategory["content"]): ContentBlock[] =>
    [...(content || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const joinServiceNames = (services: SubCategory[]) => {
    const names = services.map((service) => service.title.toLowerCase());

    if (names.length <= 1) {
        return names[0] || "";
    }

    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const getRelatedParagraph = (service: SubCategory, relatedServices: SubCategory[]) => {
    const relatedNames = joinServiceNames(relatedServices.slice(0, 2));

    if (!relatedNames) {
        return service.description;
    }

    return `${service.description} If this points to a wider ${service.headCategory.name.toLowerCase()} need, we can also advise on ${relatedNames} so the plan fits the whole business.`;
};

const getBreadcrumbData = (service: SubCategory) => {
    const categoryTitle = capitalize(service.headCategory.categoryType);
    const categoryHref = `/${service.headCategory.categoryType}`;
    const headCategoryHref = getCategoryHref(service);
    const currentHref = getServiceHref(service);

    const crumbs: Crumb[] = [
        { title: "Home", href: "/" },
        { title: categoryTitle, href: categoryHref },
        { title: service.headCategory.name, href: headCategoryHref },
        { title: service.title },
    ];

    const itemListElements = [
        {
            position: 1,
            name: "Home",
            item: getCanonicalUrl(),
        },
        {
            position: 2,
            name: categoryTitle,
            item: getCanonicalUrl(categoryHref),
        },
        {
            position: 3,
            name: service.headCategory.name,
            item: getCanonicalUrl(headCategoryHref),
        },
        {
            position: 4,
            name: service.title,
            item: getCanonicalUrl(currentHref),
        },
    ];

    return { crumbs, itemListElements };
};

const ServicesSidebar = ({
    relatedServices,
    otherServices,
}: {
    relatedServices: SubCategory[];
    otherServices: SubCategory[];
}) => (
    <div className="space-y-6">
        <SidebarArticle
            items={getSidebarArticleItems(relatedServices)}
            Link={Link}
            title="Related services"
            titleComponent="h2"
            description="More services in this category."
            itemCtaLabel="View service"
            accentColor={brandColor}
            maxWidth="100%"
            imageWidth={82}
            imageHeight={68}
            ariaLabel="Related services"
        />
        <SidebarArticle
            items={getSidebarArticleItems(otherServices)}
            Link={Link}
            title="Other services"
            titleComponent="h2"
            description="Browse other services available from our team."
            itemCtaLabel="View service"
            accentColor={brandColor}
            maxWidth="100%"
            imageWidth={82}
            imageHeight={68}
            ariaLabel="Other services"
        />
    </div>
);

const ServiceMainContent = ({ service, relatedServices }: { service: SubCategory; relatedServices: SubCategory[] }) => {
    const serviceImage = getServiceImage(service);
    const serviceAdvocateImage = getServiceAdvocateImage(service);
    const orderedContentBlocks = getOrderedContentBlocks(service.content);
    const [introContentBlock, ...remainingContentBlocks] = orderedContentBlocks;
    const relatedParagraph = getRelatedParagraph(service, relatedServices);
    const { crumbs } = getBreadcrumbData(service);
    const serviceAdvocate = service.serviceAdvocate;
    const serviceContactFormContent = createServiceContactFormContent({
        serviceTitle: service.title,
        categoryName: service.headCategory.name,
        categorySlug: service.headCategory.slug,
        serviceSlug: service.slug,
        pageUrl: service.metaTags?.url || getCanonicalUrl(getServiceHref(service)),
    });

    return (
        <div className="min-w-0 bg-white px-4 py-5 md:px-6 md:py-6">
            <div className="mb-6">
                <Modes0Breadcrumbs crumbs={crumbs} currentPageAccent={brandColor} />
            </div>
            <article className="min-w-0">
                <ContentTitles
                    title={service.title}
                    subTitle={service.description}
                    titleComponent="h1"
                    subTitleComponent="h2"
                    titleClasses="text-4xl md:text-5xl"
                    subTitleClasses="text-lg md:text-xl"
                />
                <div className="mt-6 min-w-0">
                    {serviceAdvocate && (
                        <ServicesTrustHero
                            Link={Link}
                            skyline={false}
                            image={serviceAdvocateImage}
                            badgeLabel={serviceAdvocate.badgeLabel}
                            badgeText={serviceAdvocate.badgeText}
                            title={serviceAdvocate.title}
                            titleHighlight={serviceAdvocate.titleHighlight}
                            titleComponent="h2"
                            lead={serviceAdvocate.lead}
                            description={serviceAdvocate.description}
                            benefits={serviceAdvocate.benefits ?? []}
                            primaryAction={serviceAdvocate.primaryAction ?? false}
                            secondaryAction={serviceAdvocate.secondaryAction ?? false}
                            review={false}
                            trustItems={serviceAdvocate.trustItems ?? []}
                            accentColor={brandColor}
                            backgroundColor="transparent"
                            surfaceColor="#ffffff"
                            maxWidth="100%"
                            className="service-page-advocate"
                            ariaLabel={serviceAdvocate.ariaLabel}
                        />
                    )}
                    {introContentBlock && (
                        <ContentArticleReader contentBlocks={[introContentBlock] as never} paddingY="18px" />
                    )}
                    {relatedParagraph && (
                        <p
                            className="my-6 border-l-4 bg-white px-4 py-4 text-base leading-7 text-neutral-800 md:text-lg md:leading-8"
                            style={{ borderLeftColor: brandColor }}
                        >
                            {relatedParagraph}
                        </p>
                    )}
                    {serviceImage && (
                        <figure className="my-8 overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
                            <div className="relative h-[260px] w-full md:h-[420px]">
                                <NextImage
                                    src={serviceImage.url}
                                    alt={serviceImage.alt || service.title}
                                    fill
                                    sizes="(min-width: 1024px) 820px, calc(100vw - 32px)"
                                    className="object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <figcaption className="border-t border-black/10 px-4 py-3 text-sm leading-6 text-neutral-700">
                                {serviceImage.caption ||
                                    serviceImage.description ||
                                    serviceImage.title ||
                                    `${service.title} by ${BUSINESS_NAME}.`}
                            </figcaption>
                        </figure>
                    )}
                    {remainingContentBlocks.length > 0 && (
                        <ContentArticleReader contentBlocks={remainingContentBlocks as never} paddingY="18px" />
                    )}
                </div>
            </article>
            <div id="contact" className="mt-10 border-t border-black/10 pt-8">
                <ServiceContactForm content={serviceContactFormContent} />
            </div>
        </div>
    );
};

const ServiceDetailLayout = ({ service }: { service: SubCategory }) => {
    const relatedServices = getRelatedSubCategoriesByCategory(service) || [];
    const otherServices = getAllUnRelatedSubCategoriesByCategory(service) || [];

    return (
        <section className="w-full py-10 md:py-14" style={{ backgroundColor: pageBackgroundColor }}>
            <PageWithSidebarLayout
                maxWidth="xl"
                gridTemplateColumns={{
                    lg: "2fr 1fr",
                    xl: "2fr 1fr",
                }}
                main={<ServiceMainContent service={service} relatedServices={relatedServices} />}
                sidebar={<ServicesSidebar relatedServices={relatedServices} otherServices={otherServices} />}
            />
        </section>
    );
};

export const getStaticPaths: GetStaticPaths = () => ({
    paths: getServiceSubCategories().map((service) => ({
        params: {
            categorySlug: service.headCategory.slug,
            serviceSlug: service.slug,
        },
    })),
    fallback: false,
});

export const getStaticProps: GetStaticProps<ServicePageProps> = ({ params }) => ({
    props: {
        categorySlug: String(params?.categorySlug || ""),
        serviceSlug: String(params?.serviceSlug || ""),
    },
});

export default function ServicePage({ categorySlug, serviceSlug }: ServicePageProps) {
    const service = getServiceSubCategoryBySlug(categorySlug, serviceSlug);

    if (!service) {
        return null;
    }

    const { itemListElements } = getBreadcrumbData(service);

    return (
        <>
            <PageSeo
                meta={service.metaTags}
                siteProfile={seoSiteProfile}
                schemas={[
                    {
                        id: `${service.slug}-service-json-ld`,
                        schema: buildServiceSchema(getServiceSeoInput(service), seoSiteProfile),
                    },
                    {
                        id: `${service.slug}-breadcrumbs-json-ld`,
                        schema: buildBreadcrumbSchema(getBreadcrumbSeoItems(itemListElements), seoSiteProfile),
                    },
                ]}
            />
            <ServiceDetailLayout service={service} />
        </>
    );
}
